'use client';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';
import type { PlayerLook } from '@/lib/api/rewards-hub/models';
import { AvatarPortrait } from '@/lib/components/RewardsHub/AvatarPortrait';
import { avatarArt } from '@/lib/components/RewardsHub/avatarTheme';
import { cn } from '@/lib/ui-kit/utils';
import { getAvatarByUserId } from '@/lib/utils/getAvatarByUserId';

type UserAvatarSize = 'sm' | 'md' | 'lg' | number;

interface UserAvatarProps extends React.ComponentProps<typeof AvatarPrimitive.Root> {
  userId?: string | null;
  name?: string;
  src?: string;
  size?: UserAvatarSize;
  /**
   * The Rewards Hub look (`lk`): the portrait worn, in its frames, wins over
   * `src`. The frames go on at every size — product wants them app-wide (30.10).
   */
  look?: PlayerLook | null;
}

const PRESET_SIZES: Record<'sm' | 'md' | 'lg', number> = {
  sm: 24,
  md: 36,
  lg: 44,
};

function resolveSize(size: UserAvatarSize): number {
  return typeof size === 'number' ? size : PRESET_SIZES[size];
}

function getInitials(name?: string): string {
  if (!name) return 'A';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function UserAvatar({
  userId,
  name,
  src,
  size = 'md',
  look,
  className,
  style,
  ...props
}: UserAvatarProps) {
  const pxSize = resolveSize(size);

  if (look?.avatar) {
    return (
      <AvatarPortrait
        src={avatarArt(look.avatar)}
        frames={look.avatar.frames}
        size={pxSize}
        alt={name ?? 'User avatar'}
        // Block like the Radix root: the portrait's inline-block would sit on a
        // text baseline and grow the header's avatar button on load.
        className={cn('block', className)}
      />
    );
  }

  const resolvedSrc = src ?? getAvatarByUserId(userId);

  return (
    <AvatarPrimitive.Root
      data-slot="user-avatar"
      // Sized through the same variable as the portrait, so a caller's
      // per-breakpoint `size-*` override applies to either.
      className={cn(
        'relative flex size-(--avatar-size) shrink-0 overflow-hidden rounded-full',
        className,
      )}
      style={{ '--avatar-size': `${pxSize}px`, ...style } as React.CSSProperties}
      {...props}
    >
      <AvatarPrimitive.Image
        data-slot="user-avatar-image"
        src={resolvedSrc}
        alt={name ?? 'User avatar'}
        className="aspect-square size-full object-cover"
      />
      <AvatarPrimitive.Fallback
        data-slot="user-avatar-fallback"
        delayMs={0}
        className="flex size-full items-center justify-center bg-v2-bg-blue-5 text-[12px] font-semibold text-v2-text-primary"
      >
        {getInitials(name)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export type { UserAvatarProps, UserAvatarSize };
export { getAvatarByUserId, UserAvatar };
